/**
 * Account formula edits: the signed-in customer can take optional ingredients out, pick vanilla or
 * chocolate, and pick a protein type (ACC-003, ACC-004). Removals go to a physician. Flavor and
 * protein apply from the next shipment when the formula is already signed.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { AccountFormulaEdit, AccountFormulaEditFlavor, AccountFormulaEditIngredient } from "@lattice/core";
import { ACCOUNT_FORMULA_FLAVORS } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { RadioGroup } from "@lattice/ui/RadioGroup";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { loadAccountFormula, removeAccountFormulaIngredient, restoreAccountFormulaIngredient, saveAccountFormulaOptions } from "../api.ts";
import { AccountShell } from "./Shell.tsx";
function formatDose(dose: number, unit: string): string {
  const shown = Number.isInteger(dose) ? dose.toLocaleString("en-US") : String(dose);
  return `${shown} ${unit}`;
}
function leadFor(view: AccountFormulaEdit): string {
  if (view.reviewPending) return "A physician is reviewing this change. Your signed formula keeps shipping until they sign.";
  if (view.status === "approved") return "Flavor and protein apply from your next shipment. Taking an ingredient out goes to your physician first.";
  if (view.status === "in_review") return "A physician is reviewing this formula. You can still choose flavor and protein, and take ingredients out.";
  return "These choices are saved. A physician signs the formula before it ships.";
}
const EDIT_ERRORS = {
  cannot_remove_protein: "The protein base stays in every formula.",
  unsafe: "That change did not pass the safety checks.",
  unknown_flavor: "Choose vanilla or chocolate.",
  unknown_protein: "Choose a protein the pharmacy can make.",
  failed: "We could not save that. Try again in a moment."
} as const;
function messageFor(error: string): string {
  for (const [code, text] of Object.entries(EDIT_ERRORS)) if (code === error) return text;
  return EDIT_ERRORS.failed;
}
function IngredientRow({
  item,
  busy,
  onRemove
}: {
  readonly item: AccountFormulaEditIngredient;
  readonly busy: boolean;
  readonly onRemove: (ingredientId: string) => void;
}) {
  return <li className="xrvj5dj xju1xpo x626il7 x7a106z x1wiri7k xez8t42 x13fuv20 x178xt8z" data-testid={`ingredient-${item.ingredientId}`}>
      <div className="x78zum5 xdt5ytf x626il7">
        <p className="x1ghz6dp xqu410z">{item.name}</p>
        <p className="x1ghz6dp x1l1vl4p x1jty25b xl4dl4e">{formatDose(item.dose, item.unit)}</p>
        {item.reason !== "" && <p className="x1ghz6dp x1jty25b x1fzhlzt">{item.reason}</p>}
      </div>
      <div className="x78zum5 xdt5ytf xuk3077 x1173whf">
        {!item.isProteinBase && <Button tone="quiet" size="small" busy={busy} testId={`remove-${item.ingredientId}`} onClick={() => onRemove(item.ingredientId)}>
            Remove
          </Button>}
      </div>
    </li>;
}
function flavorLabel(flavor: AccountFormulaEditFlavor): string {
  return flavor === "vanilla" ? "Vanilla" : "Chocolate";
}
type Load = "pending" | "signed_out" | "missing" | "failed" | "ready";
export function AccountFormula() {
  const [view, setView] = useState<AccountFormulaEdit | null>(null);
  const [load, setLoad] = useState<Load>("pending");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{
    readonly state: "ok" | "problem";
    readonly text: string;
  } | null>(null);
  useEffect(() => {
    document.title = "Change your formula · Lattice";
  }, []);
  useEffect(() => {
    let cancelled = false;
    void loadAccountFormula().then(result => {
      if (cancelled) return;
      if (result === "signed_out") {
        setLoad("signed_out");
        return;
      }
      if (result === "missing" || result === "failed") {
        setLoad(result);
        return;
      }
      setView(result);
      setLoad("ready");
    }, () => {
      if (!cancelled) setLoad("failed");
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const run = async (work: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      await work();
    } catch {
      setNote({
        state: "problem",
        text: "That did not go through. Try again in a minute."
      });
    } finally {
      setBusy(false);
    }
  };
  const apply = async (result: {
    readonly ok: true;
    readonly view: AccountFormulaEdit;
  } | {
    readonly ok: false;
    readonly error: string;
  }) => {
    if (result.ok) {
      setView(result.view);
      setNote(result.view.reviewPending ? {
        state: "ok",
        text: "A physician will review this change. Your signed formula keeps shipping until they sign."
      } : null);
      return;
    }
    setNote({
      state: "problem",
      text: messageFor(result.error)
    });
  };
  if (load === "pending") {
    return <AccountShell title="Change your formula">
        <Pending message="Loading your formula" bones={["title", "line", "row", "row"]} />
      </AccountShell>;
  }
  if (load === "signed_out") {
    return <AccountShell title="Change your formula" signedOut gateTitle="Sign in to change your formula">
        {null}
      </AccountShell>;
  }
  if (load === "failed") {
    return <AccountShell title="We couldn't load your formula">
        <p role="alert" className="x1ghz6dp x1jty25b x1fzhlzt">
          Try again in a minute. If it keeps happening, write to support.
        </p>
        <Button onClick={() => window.location.reload()}>Try again</Button>
      </AccountShell>;
  }
  if (load === "missing" || !view) {
    return <AccountShell title="No formula yet">
        <p className="x1ghz6dp x1jty25b x1fzhlzt">
          Upload your labs to start. A physician reviews them within 24 hours.
        </p>
        <LinkButton href="/start">Upload your labs</LinkButton>
      </AccountShell>;
  }
  return <AccountShell title="Change your formula">
      <p className="x1ghz6dp x1jty25b x1fzhlzt">{leadFor(view)}</p>
      {note && <Status state={note.state}>{note.text}</Status>}
      <section className="x78zum5 xdt5ytf x626il7" aria-labelledby="in-pouch">
        <h2 id="in-pouch" className="x1ghz6dp xqu410z">
          In the pouch
        </h2>
        <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf">
          {view.ingredients.map(item => <IngredientRow key={item.ingredientId} item={item} busy={busy} onRemove={ingredientId => void run(async () => apply(await removeAccountFormulaIngredient(ingredientId)))} />)}
        </ul>
      </section>
      {view.restorable.length > 0 && <section className="x78zum5 xdt5ytf x626il7" aria-labelledby="removed">
          <h2 id="removed" className="x1ghz6dp xqu410z">
            Removed
          </h2>
          <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf">
            {view.restorable.map(item => <li key={item.ingredientId} className="xrvj5dj xju1xpo x626il7 x7a106z x1wiri7k xez8t42 x13fuv20 x178xt8z">
                <p className="x1ghz6dp xqu410z">{item.name}</p>
                <Button tone="quiet" size="small" busy={busy} testId={`restore-${item.ingredientId}`} onClick={() => void run(async () => apply(await restoreAccountFormulaIngredient(item.ingredientId)))}>
                  Put back
                </Button>
              </li>)}
          </ul>
        </section>}
      <section className="x78zum5 xdt5ytf x626il7" aria-labelledby="protein">
        <h2 id="protein" className="x1ghz6dp xqu410z">
          Protein
        </h2>
        <p className="x1ghz6dp x1jty25b x1fzhlzt">The pharmacy can make these bases.</p>
        <RadioGroup labelledBy="protein" variant="cards" orientation="horizontal" manual loading={busy} options={view.proteinOptions.map(option => ({
        value: option.ingredientId,
        label: option.name,
        testId: `protein-${option.ingredientId}`
      }))} value={view.proteinBaseIngredientId} onChange={ingredientId => void run(async () => apply(await saveAccountFormulaOptions({
        proteinBaseIngredientId: ingredientId
      })))} />
      </section>
      <section className="x78zum5 xdt5ytf x626il7" aria-labelledby="flavor">
        <h2 id="flavor" className="x1ghz6dp xqu410z">
          Flavor
        </h2>
        {view.flavor === null && <p id="flavor-missing" className="x1ghz6dp x1jty25b x1fzhlzt">
            Not chosen yet. Choose one before your first order.
          </p>}
        <RadioGroup labelledBy="flavor" variant="cards" orientation="horizontal" describedBy={view.flavor === null ? "flavor-missing" : undefined} manual loading={busy} options={ACCOUNT_FORMULA_FLAVORS.map(flavor => ({
        value: flavor,
        label: flavorLabel(flavor),
        testId: `flavor-${flavor}`
      }))} value={view.flavor} onChange={flavor => void run(async () => apply(await saveAccountFormulaOptions({
        flavor
      })))} />
      </section>
    </AccountShell>;
}